/**
 * Cadre du back-office (docs/16).
 *
 * Volontairement distinct du `PublicLayout` : la barre latérale, la largeur
 * pleine et le contraste plus sobre servent à ce qu'un administrateur sache en
 * permanence qu'il agit **sur les comptes d'autrui** et non sur le sien. C'est
 * la même intention que le bandeau d'impersonation de docs/16 §4.
 */
import { Link, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { LocaleSwitcher } from "@/app/layouts/LocaleSwitcher";
import { cn } from "@/shared/lib/cn";

interface NavItem {
  to:
    | "/admin/users"
    | "/admin/stats"
    | "/admin/outbox"
    | "/admin/reports"
    | "/admin/feature-flags"
    | "/admin/audit-logs";
  label: string;
}

export function AdminLayout() {
  const { t } = useTranslation("admin");

  const items: NavItem[] = [
    { to: "/admin/users", label: t("nav.users") },
    { to: "/admin/stats", label: t("nav.stats") },
    { to: "/admin/outbox", label: t("nav.outbox") },
    { to: "/admin/reports", label: t("nav.reports") },
    { to: "/admin/feature-flags", label: t("nav.flags") },
    { to: "/admin/audit-logs", label: t("nav.auditLog") },
  ];

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50 dark:bg-slate-950">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow dark:focus:bg-slate-900"
      >
        {t("nav.skipToContent", { ns: "common" })}
      </a>

      <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-baseline gap-3">
            <span className="text-lg font-semibold">{t("nav.heading")}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-brand-600 text-sm hover:underline">
              {t("nav.backToApp")}
            </Link>
            <LocaleSwitcher />
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 px-4 py-8 md:flex-row">
        <nav aria-label={t("nav.heading")} className="md:w-52 md:shrink-0">
          {/* `flex-wrap` sur petit écran : la barre est verticale au-delà de
              `md`, mais horizontale en dessous — et elle porte désormais six
              entrées. Sans le retour à la ligne, les dernières sortent de
              l'écran sur un téléphone, sans barre de défilement pour les
              rattraper. La phase 11 en a ajouté deux ; c'est le moment où le
              défaut serait apparu. */}
          <ul className="flex flex-wrap gap-2 md:flex-col md:flex-nowrap">
            {items.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={cn(
                    "block rounded-md px-3 py-2 text-sm font-medium",
                    "text-slate-700 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800",
                  )}
                  activeProps={{
                    className: "bg-brand-600 text-white hover:bg-brand-700 dark:text-white",
                  }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <main id="main" className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
